/**
 * Date formatting utilities for consistent display across the application.
 * Uses Polish locale (pl-PL) for all formatting.
 */

/**
 * Format a date string for display (date only, no time).
 * Example: "15 sty 2026"
 */
export const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("pl-PL", {
    dateStyle: "medium",
  });
};

/**
 * Format a date string with time for display.
 * Example: "15 sty 2026, 14:30"
 */
export const formatDateTime = (dateStr: string): string => {
  return new Date(dateStr).toLocaleString("pl-PL", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

/**
 * Format a date string for compact display (DD.MM.YYYY).
 * Example: "15.03.2026"
 * @see docs/specifications/2026-01-30-recommendation-details-project-context.md
 */
export const formatDateCompact = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "—";
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

/**
 * Format a date range for display.
 * Example: "15.03.2026 – 30.04.2026" or "15.03.2026 –" if end missing
 * @see docs/specifications/2026-01-30-recommendation-details-project-context.md
 */
export const formatDateRange = (
  startDate: string | null | undefined,
  endDate: string | null | undefined
): string => {
  const start = formatDateCompact(startDate);
  const end = formatDateCompact(endDate);
  if (start === "—" && end === "—") return "—";
  if (start === "—") return `– ${end}`;
  if (end === "—") return `${start} –`;
  return `${start} – ${end}`;
};

/**
 * Format allocation amount for display.
 * < 1 mln: "500 000 PLN" (spaces every 3 digits)
 * >= 1 mln: "1,5 mln PLN"
 * >= 1 mld: "1,2 mld PLN"
 * @see docs/specifications/2026-01-30-recommendation-details-project-context.md
 */
export const formatAllocation = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return "—";

  const BILLION = 1_000_000_000;
  const MILLION = 1_000_000;

  if (amount >= BILLION) {
    const billions = amount / BILLION;
    return `${billions.toLocaleString("pl-PL", { maximumFractionDigits: 1 })} mld PLN`;
  }

  if (amount >= MILLION) {
    const millions = amount / MILLION;
    return `${millions.toLocaleString("pl-PL", { maximumFractionDigits: 1 })} mln PLN`;
  }

  return `${amount.toLocaleString("pl-PL")} PLN`;
};
